import type { NavigateFunction } from 'react-router';
import { DIVISION_FILTER_PARAM } from '@features/account-view/scope';
import { isLiveAtListPath } from '@utility/list-url-sync';

export type DateOperator = 'EQUAL' | 'BETWEEN' | 'LESS_THAN' | 'GREATER_THAN';

export const DATE_OPERATORS: { value: DateOperator; label: string }[] = [
  { value: 'EQUAL', label: 'On' },
  { value: 'BETWEEN', label: 'Between' },
  { value: 'LESS_THAN', label: 'Before' },
  { value: 'GREATER_THAN', label: 'After' },
];

/**
 * Operators arrive from the address bar and from the legacy filter layer, which
 * writes operators the popover has no row for. Anything unknown reads as "on".
 */
function toDateOperatorOption(value: unknown) {
  return (
    DATE_OPERATORS.find(option => option.value === value) || DATE_OPERATORS[0]
  );
}

export function toDateOperator(value: unknown): DateOperator {
  return toDateOperatorOption(value).value;
}

export type OrderFilterValues = {
  marketplace?: string;
  marketplace_region_id?: string;
  items_total_qty?: string;
  created_by?: string;
  handling_time?: string;
  handling_time_op?: string;
  shipping_time?: string;
  shipping_time_op?: string;
  fulfillment_provider_id?: string;
  /** EN-4396: comma-joined division ids; only offered at the account level. */
  division?: string;
  /** EN-4403: `exception` = only held orders (a real exception); V2 only. */
  hold?: string;
};

/** EN-4403: the `hold` value of the Held filter. */
export const HOLD_EXCEPTION = 'exception';

/* The list's state is merged into, so clearing sends empty values, not gaps. */
export const FILTER_KEYS = [
  'marketplace',
  'marketplace_region_id',
  'items_total_qty',
  'created_by',
  'handling_time',
  'handling_time_op',
  'shipping_time',
  'shipping_time_op',
  'fulfillment_provider_id',
  DIVISION_FILTER_PARAM,
  'hold',
] as const;

export const VALUE_KEYS = FILTER_KEYS.filter(key => !key.endsWith('_op'));

/* Kept behind a disclosure in the popover. */
export const ADVANCED_KEYS = [
  'items_total_qty',
  'created_by',
  'handling_time',
  'shipping_time',
] as const;

const DATE_KEYS = ['handling_time', 'shipping_time'] as const;

export type FilterOption = { value: string; label: string };

/* Legacy dropdowns spell "any" as a `-1` option; the popover has its own row. */
export function toFilterOptions(
  options: { value?: unknown; text?: unknown }[] | undefined,
): FilterOption[] {
  return (options || [])
    .map(option => ({
      value: String(option?.value ?? ''),
      label: String(option?.text ?? option?.value ?? ''),
    }))
    .filter(option => option.value !== '' && option.value !== '-1');
}

/** Multi-value filters travel as one comma-joined parameter. */
export function parseList(value: string | undefined): string[] {
  return (value || '')
    .split(',')
    .map(part => part.trim())
    .filter(Boolean);
}

export function joinList(values: string[]): string {
  return values.join(',');
}

export function toggleInList(values: string[], value: string): string[] {
  return values.includes(value)
    ? values.filter(current => current !== value)
    : [...values, value];
}

/** How many filters are set, which is what the button's badge counts. */
export function countActiveFilters(
  values: OrderFilterValues,
  keys: readonly (keyof OrderFilterValues)[] = VALUE_KEYS,
): number {
  return keys.filter(key => {
    const value = values[key];

    return typeof value === 'string' && value.trim() !== '';
  }).length;
}

export function clearFilters(): Record<string, string> {
  return Object.fromEntries(FILTER_KEYS.map(key => [key, '']));
}

/** A missing, non-numeric or out-of-range page falls back to the first page. */
export function parsePageParam(value: string | null | undefined): number {
  const page = Number(value);

  return Number.isInteger(page) && page > 0 ? page : 1;
}

/**
 * Writes the list's query onto its own route, `pathname` from the list's
 * `useLocation`. A link away moves the address bar at once, but the router
 * keeps the list mounted, at its old location, until the next route's chunk
 * has loaded; a write then would put the list's route back over that link.
 * `pushPage` set means a history entry instead of replacing the current one.
 */
export function writeFiltersToUrl(
  navigate: NavigateFunction,
  pathname: string,
  search: string,
  pushPage: { current: boolean },
) {
  if (!isLiveAtListPath(pathname)) return;
  /* The same query again changes nothing, and each write is a navigation that
     re-renders the list (and an analytics page view): skip it. */
  if (window.location.search === (search ? `?${search}` : '')) return;
  const push = pushPage.current;
  pushPage.current = false;
  navigate(search ? `?${search}` : '', { replace: !push });
}

/**
 * The query the list starts from, read off the address bar. `page`, `limit` and
 * `marketplace_region_id` are always written — the list waits for all three,
 * and the region key is what tells "every region" from "not asked yet".
 */
export function readFiltersFromUrl(
  search: URLSearchParams,
  { hasDateFilters = true }: { hasDateFilters?: boolean } = {},
): Record<string, unknown> {
  const initial: Record<string, unknown> = {
    page: parsePageParam(search.get('page')),
    limit: Number(search.get('limit')) || 50,
    marketplace_region_id: search.get('marketplace_region_id') || '',
    oos: search.get('oos') === 'true',
  };

  for (const key of FILTER_KEYS) {
    const value = search.get(key);

    if (value) {
      initial[key] = value;
    }
  }

  /* The API does nothing with a date that carries no operator, and the popover
   * has no row for the operators the legacy filters used to write. */
  for (const key of DATE_KEYS) {
    if (!hasDateFilters) {
      delete initial[key];
      delete initial[`${key}_op`];
    } else if (initial[key]) {
      initial[`${key}_op`] = toDateOperator(initial[`${key}_op`]);
    }
  }

  const sort = search.get('sort');

  if (sort) {
    try {
      initial.sort = JSON.parse(sort);
    } catch {
      /* An unparsable sort keeps the default. */
    }
  }

  return initial;
}

/* A date filter is one parameter: `BETWEEN` holds both ends, comma separated. */
export function getDateRange(value: string | undefined): [string, string] {
  const [from = '', to = ''] = parseList(value);

  return [from, to];
}

export function setDateRange(from: string, to: string): string {
  if (!from || !to) {
    return '';
  }

  return joinList([from, to]);
}

/** What a date filter says on its closed button. */
export function describeDate(
  value: string | undefined,
  operator: unknown,
  emptyText = 'Any date',
): string {
  const [from, to] = getDateRange(value);

  if (!from) {
    return emptyText;
  }

  const { value: op, label } = toDateOperatorOption(operator);

  if (op === 'BETWEEN') {
    return to ? `${from} – ${to}` : emptyText;
  }

  return `${label} ${from}`;
}

type ConnectionsAnswer =
  | { v2: false }
  | {
      v2: true;
      connections: { kind: string; provider: string | null }[];
    };

const titleCase = (word: string) =>
  word.charAt(0).toUpperCase() + word.slice(1);

/**
 * A V2 account's Marketplace filter: one option per channel family it has a
 * sales channel connection of (amazon_sp and amazon_us are Amazon), as the V2
 * list matches `marketplace` on the order's channel and its `<family>_*`.
 */
export function v2MarketplaceOptions(
  answer: ConnectionsAnswer | undefined,
): { value: string; text: string }[] {
  if (!answer?.v2) return [];
  const families = new Set(
    answer.connections
      .filter(c => c.kind === 'marketplace' && c.provider)
      .map(c => (c.provider as string).toLowerCase().split('_')[0]),
  );
  return [...families]
    .sort()
    .map(family => ({ value: family, text: titleCase(family) }));
}
